/* eslint-disable eqeqeq */
import React, { useContext, useState, useEffect, useMemo } from 'react';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Input } from 'components/formInputs/input';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PageTitle } from 'components/layouts/pageTitle';
import { useAggregate } from 'hooks';
// import { enrichProductCategoriesResponse, sortByRootParent } from 'utils/renderingHelpers';
import {
  fillTranslatableTexts,
  getValidationPairsForTranslatableText,
  getValidationShapeForTranslatableText,
  TranslationFieldOptions,
} from 'utils/schemaHelper';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { getTranslatedData } from 'locales';
import { Loader } from 'components/indicators/loader';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';

export const ProductCategoriesAddEditForm = ({ itemData, isEdit, initialRestaurant }) => {
  const navigate = useNavigate();

  const defaultLanguage = Config.DEFAULTS.LANGUAGE;

  const { state: companyState, addNew, editById, getCompanyRestaurants } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);

  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    name: yup
      .object()
      .shape(
        getValidationShapeForTranslatableText(
          itemData?.name,
          TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
        ),
        getValidationPairsForTranslatableText(),
      ),
    order: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null)),
    parent: yup.string().nullable().default(itemData?.parent || ''),
    group_category: yup.string().nullable().default(itemData?.group_category || ''),
    restaurants: yup
      .array()
      .of(yup.string())
      .required('Please enter restaurants.')
      .min(1, 'Please select at least one restaurant.'),
    rkeeper_category_id: yup.string().default(itemData?.rkeeper_category_id),
    rkeeper_system_name: yup.string().default(itemData?.rkeeper_system_name),
    hidden: yup.boolean().default(itemData?.hidden || false),
  });

  const {
    register,
    reset,
    handleSubmit,
    clearErrors,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES,
    aggregateId: Config.DATA.AGGREGATES.PRODUCT_CATEGORIES_V2,
    secretKey: auth.token,
    filter: { company: auth.company?.company, appLanguage: defaultLanguage },
    pageSize: 100,
    pageNumber: 0,
    loadOnInit: true,
    aggregateIdV2: 'AdminGetProductCategoriesHierarchy',
    companyIdForV2: auth.company?.company,
  };

  const [, productCategories, isLoadingCategories] = useAggregate(requestData);

  useEffect(() => {
    // eslint-disable-next-line no-nested-ternary
    setSelectedRestaurants(itemData ? (itemData?.restaurants || []) : initialRestaurant ? [initialRestaurant] : []);

    reset(itemData);

    if (!itemData && initialRestaurant) {
      setValue('restaurants', [initialRestaurant]);
    }
  }, [itemData, reset]);

  const onSubmit = async (data) => {
    const submitData = {
      name: fillTranslatableTexts(data.name),
      order: data.order,
      parents: data.parent ? [data.parent] : [],
      group_category: data.group_category,
      restaurants: data.restaurants || [],
      rkeeper_category_id: data.rkeeper_category_id,
      rkeeper_system_name: data.rkeeper_system_name,
      hidden: !!data.hidden,
    };

    if (!isEdit) {
      submitData.company = auth.company?.company;

      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES,
          data: submitData,
          token: auth.token,
          resourceV2: 'AdminProductCategory',
          companyIdForV2: auth.company?.company,
        },
        () => {
          navigate('/settings/product-categories');
        },
      );
    } else {
      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES,
          id: data._id,
          updateClause: {
            $set: {
              name: submitData.name,
              order: submitData.order,
              parents: submitData.parents?.map((x) => ({ $oid: x })) || [],
              restaurants: submitData.restaurants?.map((x) => ({ $oid: x })) || [],
              group_category: submitData.group_category ? { $oid: submitData.group_category } : null,
              rkeeper_category_id: submitData.rkeeper_category_id,
              rkeeper_system_name: submitData.rkeeper_system_name,
              hidden: submitData.hidden,
            },
          },
          token: auth.token,
          resourceV2: 'AdminProductCategory',
          updateTypeV2: 'General',
          updateDataV2: {
            name: submitData.name,
            order: submitData.order,
            parents: submitData.parents || [],
            restaurants: submitData.restaurants || [],
            group_category: submitData.group_category,
            rkeeper_category_id: submitData.rkeeper_category_id,
            rkeeper_system_name: submitData.rkeeper_system_name,
            hidden: submitData.hidden,
          },
          companyIdForV2: auth.company?.company,
        },
        () => {
          navigate('/settings/product-categories');
        },
      );
    }
  };
  const onError = (data) => console.log(data);

  const deleteSelectedRestaurant = (restaurantId) => {
    const newRestaurants = selectedRestaurants.filter((r) => r !== restaurantId);

    setSelectedRestaurants(newRestaurants);
    setValue('restaurants', newRestaurants);
  };

  const getRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-', address: '' }, ...companyState.restaurants];

    return allRestaurants
      ?.filter((r) => !selectedRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: `${restaurant.name} ${restaurant.address ? `(${restaurant.address})` : ''}`,
        value: restaurant._id,
      }));
  };

  const getCategoriesDataSource = () => {
    const restaurantCategories = (productCategories || []).filter((x) => x.restaurants && selectedRestaurants &&
      x.restaurants.find((y) => selectedRestaurants.includes(y)));

    const allCategories = [{ _id: '', name: '-' }, ...restaurantCategories];

    return allCategories
      .map((category) => ({
        label: getTranslatedData(category.name),
        value: category._id,
      }));
  };

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <div>
        {selectedRestaurants.map((restaurant) => {
          const restaurantData = companyState.restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurant} className="mt-2">
              <SelectedItemButton
                key={restaurantData._id}
                name={restaurantData?.name}
                onClick={() => deleteSelectedRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoadingCategories) {
    return <Loader />;
  }

  return (
    <>
      <PageTitle
        title="Product category"
        breadcrumbs={[{ path: '/settings/product-categories', title: 'Product Categories' }]}
      />
      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {isEdit
                      ? `Editing ${itemData?.name[defaultLanguage]}`
                      : 'Add new product category'}
                  </h3>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <CheckboxWrapper
                    register={register}
                    name="hidden"
                    title="Hidden"
                    subtitle="hide category and it's products from app"
                    responsiveClassName="col-span-6"
                  />

                  <div className="col-span-6 sm:col-span-3">
                    <DropDownList
                      showLabel
                      id="restaurants"
                      name="restaurants"
                      label="Restaurants"
                      options={getRestaurantsDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      onChange={(e) => {
                        setSelectedRestaurants((oldArray) => [...oldArray, e.target.value]);
                        setValue('restaurants', [...selectedRestaurants, e.target.value]);
                      }}
                    />
                    {errors?.restaurants && (
                    <p className="mt-2 text-sm text-red-600" id="email-error">
                      {errors?.restaurants.message}
                    </p>
                    )}
                    {renderSelectedRestaurants()}
                  </div>

                  <div className="col-span-6 sm:col-span-3 ">
                    <DropDownList
                      {...register('parent')}
                      showLabel
                      id="parent"
                      name="parent"
                      label="Parent Category"
                      /*
                      options={enrichProductCategoriesResponse(productCategories)
                        ?.sort(sortByRootParent)
                        ?.map((pc) => ({
                          label: pc.displayName,
                          value: pc._id,
                        }))} */
                      options={getCategoriesDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3 ">
                    <DropDownList
                      {...register('group_category')}
                      showLabel
                      id="group_category"
                      name="group_category"
                      label="Group Category"
                      subtitle="Displayed in menu under this group"
                      options={getCategoriesDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    />
                  </div>

                  <div className="hidden sm:block sm:col-span-3 " />

                  <div className="col-span-6 sm:col-span-3">
                    <TranslatableInput
                      name="name"
                      register={register}
                      error={errors?.name}
                      containerClassName="sm:col-span-3"
                      labelClassName="block text-sm font-medium text-gray-700"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Category Name*"
                      onChange={() => {
                        if (errors?.name) {
                          clearErrors('name');
                        }
                      }}
                    />
                  </div>

                  <div className="sm:col-span-3 col-span-6">
                    <label htmlFor="order" className="block text-sm font-medium text-gray-700">
                      Order
                    </label>
                    <div className="mt-1 relative text-sm text-gray-500">
                      <Input
                        {...register('order')}
                        name="order"
                        id="order"
                        type="text"
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        errorMessage={errors?.order}
                      />
                    </div>
                    {errors?.order && (
                      <p className="mt-2 text-sm text-red-600" id="order-error">
                        {errors?.order.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="col-span-6">
                  <fieldset className="mt-10">
                    <div className="mb-6">
                      <legend className="text-base font-medium text-gray-900">External category (optional)</legend>
                    </div>
                    <div className="grid grid-cols-6 gap-6">
                      <TextboxWrapper
                        register={register}
                        name="rkeeper_system_name"
                        title="External System ID"
                        responsiveClassName="col-span-6 sm:col-span-3"
                        error={errors?.rkeeper_system_name}
                      />
                      <TextboxWrapper
                        register={register}
                        name="rkeeper_category_id"
                        title="External ID"
                        responsiveClassName="col-span-6 sm:col-span-3"
                        error={errors?.rkeeper_category_id}
                      />
                    </div>
                  </fieldset>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => navigate('/settings/product-categories')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {isEdit ? 'Save' : 'Add new'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
